import { useTranslation } from 'react-i18next';

/**
 * Shared types for the article-page template (Technology, Our story,
 * Patient care, Contact us). All four pages render the same `ArticlePage`
 * component with their own data.
 */

export type ArticleBodyBlock =
  | {
      /** A standard paragraph rendered with `leading-[48px] mb-[40px]`. */
      type: 'paragraph';
      text: string;
    }
  | {
      /**
       * A "Channel N — text" emphasis line. The `label` renders bold, an
       * em-dash separator follows, and `text` continues in regular weight.
       * Used in the Tri-Channel section of the Technology page.
       */
      type: 'channel';
      label: string;
      text: string;
    };

export interface ArticleSectionData {
  /** Numbered eyebrow, e.g. "01.". */
  number: string;
  title: string;
  subtitle: string;
  keyOutcomes: readonly string[];
  body: readonly ArticleBodyBlock[];
}

export interface ArticlePageData {
  /** Page-level heading, e.g. "Technology". */
  pageTitle: string;
  /** Page-level intro paragraph (right column of the page header). */
  intro: string;
  sections: readonly ArticleSectionData[];
  /** Optional trademark / legal disclaimer rendered at the foot of the page. */
  disclaimer?: string;
}

/**
 * Sibling page links — kept for completeness/legacy. The article-style
 * pages currently render a single right-aligned "View catalogue" button
 * via `useViewCatalogueLink()` instead of these.
 */
export const ARTICLE_LINKS = [
  { label: 'Technology', to: '/technology' },
  { label: 'Our story', to: '/our-story' },
  { label: 'Patient care', to: '/patient-care' },
  { label: 'Contact us', to: '/contact' },
] as const;

/**
 * Single-link list used by every non-Products page in its `PageHeader`.
 * Returns the translated label so the link reads natively at the active
 * language. The wrapping array is so the result drops straight into
 * `PageHeader`'s `links` prop without callers re-wrapping it.
 */
export function useViewCatalogueLink(): readonly { label: string; to: string }[] {
  const { t } = useTranslation();
  return [{ label: t('common.viewCatalogue'), to: '/products' }];
}

/**
 * Static fallback for the rare consumer that needs the link list outside a
 * React component (e.g. a constant export). Prefer `useViewCatalogueLink()`
 * inside components — its label updates with the active language.
 */
export const VIEW_CATALOGUE_LINK = [{ label: 'View our catalogue', to: '/products' }] as const;
